import React from 'react';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useTaskStore } from '../stores/useTaskStore';
import { useDocumentStore } from '../stores/useDocumentStore';
import { useUIStore } from '../stores/useUIStore';
import { TaskStatus } from '../types/spec';
import { CheckCircle2, Clock, ShieldAlert, XCircle, Circle, Filter, ArrowUpRight, CheckSquare } from 'lucide-react';

export const TaskMatrixView: React.FC = () => {
  const { currentPackage } = useWorkspaceStore();
  const { tasks, statusFilter, setStatusFilter, updateTaskStatus, getMetrics } = useTaskStore();
  const { setActiveDocument } = useDocumentStore();
  const { setActiveTab } = useUIStore();

  const packageTasks = tasks.length > 0 ? tasks : currentPackage?.taskMatrix || [];
  const metrics = getMetrics();

  const filteredTasks = packageTasks.filter((task) => {
    if (statusFilter === 'all') return true;
    return task.status === statusFilter;
  });

  const getStatusBadge = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return { icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />, label: 'Completed', class: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' };
      case 'in-progress':
        return { icon: <Clock className="w-3.5 h-3.5 text-amber-400 animate-pulse" />, label: 'In Progress', class: 'bg-amber-500/10 text-amber-400 border-amber-500/30' };
      case 'blocked':
        return { icon: <ShieldAlert className="w-3.5 h-3.5 text-red-400" />, label: 'Blocked', class: 'bg-red-500/10 text-red-400 border-red-500/30' };
      case 'cancelled':
        return { icon: <XCircle className="w-3.5 h-3.5 text-slate-400" />, label: 'Cancelled', class: 'bg-slate-500/10 text-slate-400 border-slate-500/30' };
      default:
        return { icon: <Circle className="w-3.5 h-3.5 text-slate-400" />, label: 'Open', class: 'bg-slate-500/10 text-slate-300 border-slate-500/30' };
    }
  };

  const handleNavigateToDoc = (docPath: string) => {
    setActiveDocument(docPath);
    setActiveTab('reader');
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 max-w-6xl mx-auto space-y-6">
      {/* Task Dashboard KPI Summary Header */}
      <div className="glass-panel p-6 rounded-2xl border border-white/10 space-y-5 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-extrabold text-white tracking-tight flex items-center space-x-2">
              <CheckSquare className="w-5 h-5 text-emerald-400" />
              <span>Master Task Matrix & Implementation Progress</span>
            </h2>
            <p className="text-xs text-slate-400 mt-1 font-mono">
              Live status tracking across all specification documents in {currentPackage?.name || 'Workspace'}.
            </p>
          </div>

          <div className="text-right">
            <div className="text-3xl font-extrabold text-emerald-400 font-mono tracking-tight">
              {metrics.percentComplete}%
            </div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest font-mono">
              Completion Progress
            </div>
          </div>
        </div>

        {/* Milestone Progress Bar */}
        <div className="space-y-1">
          <div className="w-full bg-obsidian-950 h-3.5 rounded-full overflow-hidden border border-slate-800 flex shadow-inner p-0.5">
            <div style={{ width: `${(metrics.completed / (metrics.total || 1)) * 100}%` }} className="bg-emerald-500 h-full rounded-full transition-all duration-500" title="Completed" />
            <div style={{ width: `${(metrics.inProgress / (metrics.total || 1)) * 100}%` }} className="bg-amber-500 h-full transition-all duration-500" title="In Progress" />
            <div style={{ width: `${(metrics.blocked / (metrics.total || 1)) * 100}%` }} className="bg-red-500 h-full transition-all duration-500" title="Blocked" />
            <div style={{ width: `${(metrics.open / (metrics.total || 1)) * 100}%` }} className="bg-slate-700 h-full transition-all duration-500" title="Open" />
          </div>
        </div>

        {/* 5-State KPI Pill Cards */}
        <div className="grid grid-cols-5 gap-3 pt-2 font-mono text-xs">
          <button
            onClick={() => setStatusFilter('all')}
            className={`p-3 rounded-xl border text-left transition-all ${
              statusFilter === 'all' ? 'bg-blue-600/20 border-blue-500 text-white shadow-lg shadow-blue-500/10' : 'bg-obsidian-900/60 border-slate-800 text-slate-400 hover:border-slate-700'
            }`}
          >
            <div className="text-[10px] uppercase text-slate-500 font-semibold">All Tasks</div>
            <div className="text-xl font-bold text-white mt-0.5">{metrics.total}</div>
          </button>

          <button
            onClick={() => setStatusFilter('completed')}
            className={`p-3 rounded-xl border text-left transition-all ${
              statusFilter === 'completed' ? 'bg-emerald-600/20 border-emerald-500 text-white shadow-lg shadow-emerald-500/10' : 'bg-obsidian-900/60 border-slate-800 text-emerald-400 hover:border-slate-700'
            }`}
          >
            <div className="text-[10px] uppercase text-slate-500 font-semibold">✓ Completed</div>
            <div className="text-xl font-bold text-emerald-400 mt-0.5">{metrics.completed}</div>
          </button>

          <button
            onClick={() => setStatusFilter('in-progress')}
            className={`p-3 rounded-xl border text-left transition-all ${
              statusFilter === 'in-progress' ? 'bg-amber-600/20 border-amber-500 text-white shadow-lg shadow-amber-500/10' : 'bg-obsidian-900/60 border-slate-800 text-amber-400 hover:border-slate-700'
            }`}
          >
            <div className="text-[10px] uppercase text-slate-500 font-semibold">⏳ In Progress</div>
            <div className="text-xl font-bold text-amber-400 mt-0.5">{metrics.inProgress}</div>
          </button>

          <button
            onClick={() => setStatusFilter('blocked')}
            className={`p-3 rounded-xl border text-left transition-all ${
              statusFilter === 'blocked' ? 'bg-red-600/20 border-red-500 text-white shadow-lg shadow-red-500/10' : 'bg-obsidian-900/60 border-slate-800 text-red-400 hover:border-slate-700'
            }`}
          >
            <div className="text-[10px] uppercase text-slate-500 font-semibold">🚫 Blocked</div>
            <div className="text-xl font-bold text-red-400 mt-0.5">{metrics.blocked}</div>
          </button>

          <button
            onClick={() => setStatusFilter('open')}
            className={`p-3 rounded-xl border text-left transition-all ${
              statusFilter === 'open' ? 'bg-slate-700/40 border-slate-500 text-white shadow-lg' : 'bg-obsidian-900/60 border-slate-800 text-slate-300 hover:border-slate-700'
            }`}
          >
            <div className="text-[10px] uppercase text-slate-500 font-semibold">○ Open</div>
            <div className="text-xl font-bold text-slate-300 mt-0.5">{metrics.open}</div>
          </button>
        </div>
      </div>

      {/* Task List Matrix Table */}
      <div className="glass-panel rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
        <div className="px-6 py-3.5 border-b border-white/10 flex items-center justify-between bg-obsidian-900/60 text-xs text-slate-400 font-mono">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-blue-400" />
            <span>Filter Status: <strong className="text-white uppercase font-bold">{statusFilter}</strong></span>
          </div>
          <span>Showing {filteredTasks.length} of {packageTasks.length} tasks</span>
        </div>

        <div className="divide-y divide-white/5">
          {filteredTasks.map((task) => {
            const badge = getStatusBadge(task.status);
            return (
              <div key={task.id} className="p-4 hover:bg-slate-800/40 transition-colors flex items-center justify-between text-xs group">
                <div className="flex items-center space-x-3.5 flex-1 min-w-0 pr-4">
                  {/* Status Mutation Button */}
                  <button
                    onClick={() => {
                      const nextStatus: Record<TaskStatus, TaskStatus> = {
                        open: 'in-progress',
                        'in-progress': 'completed',
                        completed: 'open',
                        blocked: 'in-progress',
                        cancelled: 'open'
                      };
                      updateTaskStatus(task.id, nextStatus[task.status]);
                    }}
                    className={`px-3 py-1.5 rounded-lg border font-mono flex items-center space-x-1.5 transition-all ${badge.class}`}
                    title="Click to cycle task status"
                  >
                    {badge.icon}
                    <span className="font-bold text-[11px] uppercase tracking-wider">{badge.label}</span>
                  </button>

                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-slate-200 truncate ${task.status === 'cancelled' ? 'line-through text-slate-500' : ''}`}>
                      {task.description}
                    </p>
                    <div className="flex items-center space-x-2 mt-1 text-[11px] text-slate-400 font-mono">
                      <span className="text-blue-400 font-medium">{task.documentPath}</span>
                      <span>•</span>
                      <span>Line {task.lineNumber}</span>
                      {task.category && (
                        <>
                          <span>•</span>
                          <span className="px-2 py-0.2 rounded bg-slate-800 text-slate-300 border border-slate-700 font-bold">{task.category}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Deep Link to Document */}
                <button
                  onClick={() => handleNavigateToDoc(task.documentPath)}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg border border-slate-700 flex items-center space-x-1.5 transition-all text-[11px] font-mono shrink-0 hover:border-cyan-500/50"
                >
                  <span>View Doc</span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-cyan-400" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
